import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { toast } from 'react-toastify';
import {
    useUpdateAgentAvailabileService,
    useGetCallDetails,
    useGetQualifications,
    useUpdateCallStatus,
    useGetCallTranscript,
    useManualCallback,
} from 'services/hooks/mutations';
import { useGetAgregatedQualifications } from 'services/hooks/mutations/useGetAgregatedQualifications';
import { useLiveKitRoom } from 'hooks';
import { ConnectionState } from 'livekit-client';
import { WSMessageType } from 'types/websocket.type';
import { formatQueueDelayAlertToast } from 'utils/queueDelayAlert.utils';
import type { CallDetailsResponse, AgregatedQualificationResponse } from 'services/api/AgentCenterAPI';
import { HEARTBEAT_TIMEOUT, AUTO_REJECT_TIMEOUT, HEARTBEAT_INTERVAL, RINGING_INTERVAL } from 'common/constants';
import { CallType } from 'types/call.type';
import { formatRelative } from 'date-fns';
import {
    createWebAudioRinging,
    createWebAudioBeeping,
    formatTranscriptData,
    populateWarningMessage,
    formatQualifications,
} from 'utils/livekitContext.utils';
import { useLogging } from './LoggingContext';

export type FormattedQualifications = {
    key: string;
    label: string;
    value: string;
    isTrue: boolean;
    isFalse: boolean;
    isUnknown: boolean;
};

export type FormattedTranscriptSpeech = {
    sender: string;
    text: string;
    timestamp: string;
    order: number;
};

export type ActiveCallData = {
    callID: string;
    partyID: string;
    roomName: string;
    isManualCallback?: boolean;
};

export const CallStatuses = {
    INITIALIZING: 'initializing',
    INCOMING: 'incoming',
    IN_PROGRESS: 'in-progress',
    MANUAL_CALLBACK: 'manual_callback',
    OUTBOUND_RINGING: 'outbound_ringing',
    READY: 'ready',
    BUSY: 'busy',
    TRANSFERRED: 'transferred',
    ERROR: 'error',
} as const;

export type CallStatusType = (typeof CallStatuses)[keyof typeof CallStatuses];

export type CallDetailsWithParentDetails = CallDetailsResponse['callDetails'] & {
    parentCallDetails?: CallDetailsResponse['callDetails'];
};

// Define the context type
export type LiveKitContextType = {
    room: any;
    callStatus: CallStatusType;
    transferTarget: string | null;
    activeCallData: ActiveCallData | null;
    agentAvailable: boolean;
    callTranscript: FormattedTranscriptSpeech[];
    callDetails: CallDetailsWithParentDetails | null;
    availableStatuses: CallDetailsResponse['statuses'];
    partyInfo: CallDetailsResponse['partyInfo'] | null;
    qualifications: FormattedQualifications[];
    aggregatedTagsUsed: boolean;
    aggregatedQualifications: AgregatedQualificationResponse[];
    isQualificationsLoading: boolean;
    shouldCloseCallConsole: boolean;
    setShouldCloseCallConsole: (shouldClose: boolean) => void;
    clientInfo: {
        name: string;
        phoneNumber: string;
        workflow: string;
        callDirection?: string;
        isParentCall?: boolean;
        parentCallDate?: string;
        parentWorkflowName?: string;
    };
    mute: () => Promise<void>;
    unmute: () => Promise<void>;
    hangUp: () => Promise<void>;
    acceptCall: () => Promise<void>;
    rejectCall: () => Promise<void>;
    makeCall: (
        callId: string,
        callbacks?: { onSuccess?: () => void; onError?: (error: Error) => void }
    ) => Promise<void>;
    updateAgentAvailability: (isAvailable: boolean) => Promise<void>;
    wsReadyState: ReadyState;
    forceReconnect: () => void;
};

// Create the LiveKit context
const LiveKitContext = createContext<LiveKitContextType | null>(null);

// LiveKit provider component
export const LiveKitProvider = ({ children }: { children: React.ReactNode }) => {
    const { info, warn, error: logError } = useLogging();

    // State for room and call status
    const [callStatus, setCallStatus] = useState<CallStatusType>(CallStatuses.INITIALIZING);
    const [transferTarget, setTransferTarget] = useState<string | null>(null);
    const [wsUrl, setWsUrl] = useState<string | null>(null);
    const [shouldConnect, setShouldConnect] = useState(false);
    const [activeCallData, setActiveCallData] = useState<ActiveCallData | null>(null);
    // Default agent availability to true
    const [agentAvailable, setAgentAvailable] = useState(false);
    const [pendingCallCredentials, setPendingCallCredentials] = useState<{ url: string; token: string } | null>(null);
    const [callTranscript, setCallTranscript] = useState<FormattedTranscriptSpeech[]>([]);
    const [callDetails, setCallDetails] = useState<CallDetailsWithParentDetails | null>(null);
    const [availableStatuses, setAvailableStatuses] = useState<CallDetailsResponse['statuses']>([]);
    const [partyInfo, setPartyInfo] = useState<CallDetailsResponse['partyInfo'] | null>(null);
    const [qualificationsData, setQualificationsData] = useState<FormattedQualifications[]>([]);
    const [aggregatedTagsUsed, setAggregatedTagsUsed] = useState(false);
    const [aggregatedQualificationsData, setAggregatedQualificationsData] = useState<AgregatedQualificationResponse[]>(
        []
    );
    const [isQualificationsLoading, setIsQualificationsLoading] = useState(false);
    const [shouldCloseCallConsole, setShouldCloseCallConsole] = useState(false);

    // Create a ref to track the current activeCallData to avoid closure issues
    const activeCallDataRef = useRef<ActiveCallData | null>(null);

    // Reference for auto-reject timer
    const callTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Reference for ringing interval
    const ringingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Reference for beeping interval (outbound calls)
    const beepingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Reference for beeping timeout
    const beepingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Add refs for connection management
    const reconnectCountRef = useRef(0);
    const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const connectionErrorsRef = useRef(0);

    // Set while the document is unloading so onClose skips the user-facing warning
    const isUnloadingRef = useRef(false);

    // Live socket, captured from the open event - `getWebSocket()` reads the hook's internal
    // ref, which is null unless that hook instance created the connection itself
    const socketRef = useRef<WebSocket | null>(null);

    // Function to stop beeping
    const stopBeeping = () => {
        if (beepingIntervalRef.current) {
            clearInterval(beepingIntervalRef.current);
            beepingIntervalRef.current = null;
        }
        if (beepingTimeoutRef.current) {
            clearTimeout(beepingTimeoutRef.current);
            beepingTimeoutRef.current = null;
        }
    };

    const { connectToLiveKitRoom, room, livekitRoomRef } = useLiveKitRoom({
        setCallStatus,
        setTransferTarget,
        setCallTranscript,
        setCallDetails,
        setAvailableStatuses,
        setPartyInfo,
        setQualificationsData,
        activeCallDataRef,
        stopBeeping,
        setShouldCloseCallConsole,
    });
    const { updateAgentAvailabileService } = useUpdateAgentAvailabileService();
    const { getCallDetails } = useGetCallDetails();
    const { getQualifications } = useGetQualifications();
    const { getAgregatedQualifications } = useGetAgregatedQualifications();
    const { updateCallStatus } = useUpdateCallStatus();
    const { initiateManualCallback } = useManualCallback();
    const { getCallTranscriptWithRetry } = useGetCallTranscript();

    // Reset call related data
    const resetCallData = () => {
        setCallStatus(CallStatuses.READY);
        setPendingCallCredentials(null);
        setActiveCallData(null);
        setCallTranscript([]);
        setCallDetails(null);
        setAvailableStatuses([]);
        setPartyInfo(null);
        setQualificationsData([]);
        setAggregatedTagsUsed(false);
        setAggregatedQualificationsData([]);
        setIsQualificationsLoading(false);
    };

    const resetCallTimeouts = () => {
        // Clear auto-reject timer
        if (callTimeoutRef.current) {
            clearTimeout(callTimeoutRef.current);
            callTimeoutRef.current = null;
        }

        // Clear ringing interval
        if (ringingIntervalRef.current) {
            clearInterval(ringingIntervalRef.current);
            ringingIntervalRef.current = null;
        }

        // Clear beeping interval
        if (beepingIntervalRef.current) {
            clearInterval(beepingIntervalRef.current);
            beepingIntervalRef.current = null;
        }

        // Clear beeping timeout
        if (beepingTimeoutRef.current) {
            clearTimeout(beepingTimeoutRef.current);
            beepingTimeoutRef.current = null;
        }
    };

    // Function to start beeping for outbound calls
    const startBeeping = () => {
        // Clear any existing beeping
        if (beepingIntervalRef.current) {
            clearInterval(beepingIntervalRef.current);
            beepingIntervalRef.current = null;
        }

        createWebAudioBeeping();
        // Start beeping interval
        beepingIntervalRef.current = setInterval(() => {
            try {
                createWebAudioBeeping();
            } catch (error) {
                console.warn('Error playing beeping tone:', error);
            }
        }, 4500);
    };

    // Function to retry qualifications fetch with delay
    const retryFetchQualifications = async (callId: string, useAggregatedTags: boolean) => {
        let attempts = 0;
        const maxRetries = 6;
        const delayMs = 5000;

        setIsQualificationsLoading(true);
        setQualificationsData([]);
        setAggregatedQualificationsData([]);

        while (attempts < maxRetries) {
            try {
                attempts++;

                if (useAggregatedTags) {
                    const aggregatedQualificationsResponse = await getAgregatedQualifications(callId);

                    if (aggregatedQualificationsResponse?.length > 0) {
                        setAggregatedQualificationsData(aggregatedQualificationsResponse);
                        setIsQualificationsLoading(false);

                        info(
                            'Successfully retrieved aggregated qualifications:',
                            { aggregatedQualificationsResponse, callId },
                            'LiveKitProvider',
                            'get_aggregated_qualifications_success'
                        );

                        return true;
                    }
                } else {
                    const qualificationsResponse = await getQualifications(callId);

                    if (qualificationsResponse?.length > 0) {
                        const formattedQualifications: FormattedQualifications[] =
                            formatQualifications(qualificationsResponse);

                        setQualificationsData(formattedQualifications);
                        setIsQualificationsLoading(false);

                        info(
                            'Successfully retrieved qualifications:',
                            { qualificationsResponse, formattedQualifications },
                            'LiveKitProvider',
                            'get_qualifications_success'
                        );

                        return true;
                    }
                }
            } catch (error) {
                console.error(`Error fetching qualifications (attempt ${attempts}):`, error);
            }

            if (attempts < maxRetries) {
                console.log(`Waiting ${delayMs}ms before next qualification attempt...`);
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
        }

        setIsQualificationsLoading(false);
        console.error(`Failed to get qualifications after ${maxRetries} attempts`);
        warn(
            `Failed to get qualifications after ${maxRetries} attempts`,
            { maxRetries, callId, useAggregatedTags },
            'LiveKitProvider',
            'get_qualifications_failed'
        );
        return false;
    };

    // Initialize WebSocket connection after authentication
    useEffect(() => {
        const initializeConnection = async () => {
            try {
                // Use the environment variable for API URL
                const apiUrl = process.env.REACT_APP_API_URL;
                // Convert from http:// to ws:// for WebSocket connection
                const wsUrl = apiUrl?.replace(/^http/, 'ws');

                // Set the WebSocket URL with token as query parameter
                setWsUrl(`${wsUrl}/ws/connect`);
                setShouldConnect(true);
            } catch (error) {
                console.error('Error initializing connection:', error);
                setCallStatus(CallStatuses.INITIALIZING);
            }
        };

        initializeConnection();
    }, []);

    // WebSocket message handler
    const handleWebSocketMessage = useCallback(
        async (event: MessageEvent) => {
            if (event.data === 'pong') {
                console.log('Message received:', event.data);
                return;
            }

            let data: { type: string; data: Record<string, any> } = { type: '', data: {} as Record<string, any> };
            // Process incoming data from the server
            try {
                info('WebSocket message received:', { event }, 'LiveKitProvider', 'ws_message_received');
                data = JSON.parse(event.data);
                info('Parsed WebSocket message:', { data }, 'LiveKitProvider', 'parsed_ws_message_received');
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
                warn(
                    'Error parsing WebSocket message:',
                    { error, event },
                    'LiveKitProvider',
                    'error_parsing_web_socket_message'
                );
                return;
            }

            // Handle incoming call
            if (data.type === WSMessageType.IncomingCall) {
                const { livekitURL, token, callID, partyID } = data.data;
                info(
                    'Incoming call received:',
                    { livekitURL, callID, partyID, callStatus, agentAvailable },
                    'LiveKitProvider',
                    'incoming_call_received'
                );

                // Only process incoming calls if agent is available
                if (agentAvailable && callStatus !== CallStatuses.IN_PROGRESS) {
                    // Store call data
                    setActiveCallData({
                        callID,
                        partyID,
                        roomName: data.data.room,
                    });

                    // Reset call timeouts if any are set
                    resetCallTimeouts();

                    // Play ringing sound repeatedly
                    ringingIntervalRef.current = setInterval(() => {
                        try {
                            createWebAudioRinging();
                        } catch (error) {
                            console.warn('Error playing ringing tone:', error);
                        }
                    }, RINGING_INTERVAL);

                    await getCallDetails(callID, {
                        onSuccess: async (data) => {
                            // Update call status to incoming
                            setCallStatus(CallStatuses.INCOMING);
                            info(
                                'Call status set to incoming:',
                                { callStatus: CallStatuses.INCOMING, callID, data },
                                'LiveKitProvider',
                                'call_status_set_to_incoming'
                            );

                            getCallTranscriptWithRetry(callID, {
                                onSuccess: (transcriptData) => {
                                    setCallTranscript(formatTranscriptData(transcriptData));
                                    info(
                                        'Successfully fetched call transcript:',
                                        { activeCallData, transcriptData },
                                        'LiveKitProvider',
                                        'success_fetching_call_transcript'
                                    );
                                },
                                onError: (error) => {
                                    console.error('Error fetching call transcript:', error);
                                    setCallTranscript([]);
                                    warn(
                                        'Error fetching call transcript:',
                                        { activeCallData: activeCallDataRef.current },
                                        'LiveKitProvider',
                                        'error_fetching_call_transcript'
                                    );
                                },
                            });

                            setCallDetails(data.callDetails);

                            // Store available statuses
                            setAvailableStatuses(data.statuses || []);

                            // Store party info
                            setPartyInfo(data.partyInfo);
                            setAggregatedTagsUsed(data.aggregatedTagsUsed);

                            // Set a timer to auto-reject call after 10 seconds if not answered
                            callTimeoutRef.current = setTimeout(() => {
                                info(
                                    `Call auto-rejected after ${AUTO_REJECT_TIMEOUT} seconds timeout`,
                                    { activeCallData: activeCallDataRef.current },
                                    'LiveKitProvider',
                                    `call_auto_rejected_after_${AUTO_REJECT_TIMEOUT}_seconds_timeout`
                                );
                                rejectCall();
                            }, AUTO_REJECT_TIMEOUT);

                            info(
                                'Successfully fetched call details:',
                                { callDetails: data.callDetails, callID },
                                'LiveKitProvider',
                                'success_fetching_call_details'
                            );

                            // Fetch qualifications with retry
                            retryFetchQualifications(callID, data.aggregatedTagsUsed);
                        },
                        onError: (error) => {
                            console.error('Error fetching call details:', error);
                        },
                    });

                    // Store the credentials but don't connect yet
                    setPendingCallCredentials({ url: livekitURL, token });

                    // Don't connect to LiveKit room here - wait for user to accept
                } else if (callStatus === CallStatuses.IN_PROGRESS) {
                    info(
                        'Call is in progress, ignoring incoming call',
                        { callID, callStatus, agentAvailable },
                        'LiveKitProvider',
                        'call_is_in_progress_ignoring_incoming_call'
                    );
                } else {
                    console.log('Agent is not available, ignoring incoming call');
                    info(
                        'Agent is not available, ignoring incoming call',
                        { callID, callStatus, agentAvailable },
                        'LiveKitProvider',
                        'agent_is_not_available_ignoring_incoming_call'
                    );
                }
            } else if (data.type === WSMessageType.DropCall) {
                const { callID } = data.data;
                // Read from ref to avoid stale closure — DropCall can race with
                // the setActiveCallData from the preceding IncomingCall handler.
                const currentCallData = activeCallDataRef.current;

                info('DropCall message received', { callID, currentCallData }, 'LiveKitProvider', 'drop_call_received');

                if (callID === currentCallData?.callID) {
                    info(
                        'Transfer user abandoned, callID:',
                        { callID, currentCallData },
                        'LiveKitProvider',
                        'transfer_user_abandoned'
                    );

                    toast.warning('The user has abandoned the call.');

                    // Stop ringing sound and auto-reject timer
                    resetCallTimeouts();

                    // if the room is connected, disconnect it
                    // disconnect event handler in useLiveKitRoom will handle the rest
                    // otherwise, reset the call data
                    if (livekitRoomRef.current?.state === ConnectionState.Connected) {
                        livekitRoomRef.current.disconnect();
                    } else {
                        resetCallData();
                    }
                } else {
                    warn(
                        'DropCall ignored — callID does not match active call',
                        { callID, currentCallData },
                        'LiveKitProvider',
                        'drop_call_call_id_mismatch'
                    );
                }
            } else if (data.type === WSMessageType.TerminateManualCall) {
                const { status } = data.data;

                const statusBusy = status === 'BUSY';
                const statusFailed = status === 'FAILED';

                const toastMessage = statusFailed
                    ? 'Sorry, call failed. Please try again later.'
                    : statusBusy
                      ? 'Sorry, the user is busy. Please try again later.'
                      : 'Sorry, call terminated. Please try again later.';

                hangUp();

                toast.success(toastMessage);
            } else if (data.type === WSMessageType.QueueDelayAlert) {
                toast.warning(formatQueueDelayAlertToast(data));
            }
        },
        [agentAvailable, callStatus, activeCallData?.callID]
    );
    // Using the react-use-websocket hook with smart heartbeat
    const { readyState } = useWebSocket(
        wsUrl,
        {
            heartbeat: {
                interval: HEARTBEAT_INTERVAL,
                timeout: HEARTBEAT_TIMEOUT,
                message: 'ping',
                returnMessage: 'pong',
            },
            onOpen: (event) => {
                // The open event fires on the socket itself - keep it for the unload handler
                socketRef.current = event.target instanceof WebSocket ? event.target : null;
                isUnloadingRef.current = false;

                console.log('WebSocket connection opened', event);
                info('WebSocket connection opened', { event }, 'LiveKitProvider', 'ws_connection_opened');
                // Reset reconnect count on successful connection
                reconnectCountRef.current = 0;

                // Clear any manual reconnection timers
                if (reconnectTimerRef.current) {
                    clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = null;
                }
            },
            onMessage: handleWebSocketMessage,
            onError: (event) => {
                console.error('WebSocket error:', event);
                logError('WebSocket error:', new Error(event.toString()), { event }, 'LiveKitProvider', 'ws_error');
                toast.error('WebSocket error');
                // Increment error count for monitoring connection health
                connectionErrorsRef.current += 1;
            },
            onClose: (event) => {
                socketRef.current = null;

                console.log('WebSocket connection closed. Code:', event.code, 'Reason:', event.reason);
                info('WebSocket connection closed', { event }, 'LiveKitProvider', 'ws_connection_closed');

                if (shouldConnect && agentAvailable && !isUnloadingRef.current) {
                    toast.warning(populateWarningMessage(event), {
                        position: 'top-right',
                        autoClose: false,
                        closeOnClick: true,
                    });

                    // Automatically set agent availability to offline
                    updateAgentAvailability(false);
                }
                console.log('WebSocket readyState on close:', readyState);

                if (event.code === 1006) {
                    console.warn(
                        'WebSocket closed abnormally (code 1006) - may indicate network issues or server disconnect'
                    );
                }
                if (event.code === 1001) {
                    console.log('WebSocket closed due to client going away (code 1001)');
                }
                if (livekitRoomRef.current) {
                    console.log('LiveKit room state on WebSocket close:', livekitRoomRef.current.state);
                }
            },
            shouldReconnect: (closeEvent) => {
                // Always attempt to reconnect unless it's a normal closure (1000) or going away (1001)
                return closeEvent.code !== 1000 && closeEvent.code !== 1001;
            },
            reconnectAttempts: 10, // Increased from 5 to 10
            // Use an exponential backoff strategy
            reconnectInterval: (attemptNumber) => Math.min(3000 * Math.pow(1.5, attemptNumber), 30000),
            // Remove the headers option as it won't work for WebSocket connections
            share: false,
            filter: () => false,
            retryOnError: true,
            skipAssert: false,
        },
        agentAvailable && shouldConnect
    );

    // Close the socket on document unload so the backend unregisters the client immediately.
    // react-use-websocket only closes on unmount, and Chrome does not finish the close
    // handshake on cross-origin navigations (e.g. typing staging.slai while on develop.slai),
    // leaving the connection half-open until the server's 130s read deadline.
    // `pagehide` still fires there, unlike `beforeunload`.
    useEffect(() => {
        const handlePageHide = () => {
            // `event.persisted` is true for these navigations too (the page is bfcache
            // eligible, not necessarily discarded), so always close - a frozen page cannot
            // keep the connection healthy anyway
            const socket = socketRef.current;
            if (socket?.readyState !== WebSocket.OPEN) {
                return;
            }

            isUnloadingRef.current = true;

            // Must close synchronously - the document may be discarded after this stack.
            // 1001 Going Away - `shouldReconnect` skips reconnecting on this code
            socket.close(1001, 'pagehide');
        };

        window.addEventListener('pagehide', handlePageHide);

        return () => window.removeEventListener('pagehide', handlePageHide);
    }, []);

    // Function to force reconnection - can be exposed to UI for manual reconnect button
    const forceReconnect = useCallback(() => {
        setShouldConnect(false); // Suspend connection
        setTimeout(() => {
            setShouldConnect(true); // Resume with fresh connection
        }, 500);
    }, []);

    // Keep activeCallDataRef in sync with activeCallData
    useEffect(() => {
        console.log('activeCallData changed:', activeCallData);
        activeCallDataRef.current = activeCallData;
    }, [activeCallData]);

    // Function to accept incoming call
    const acceptCall = useCallback(async () => {
        info('Accepting incoming call', { activeCallData }, 'LiveKitProvider', 'accept_call');
        try {
            // Stop ringing sound and auto-reject timer
            resetCallTimeouts();

            // Immediately stop ringing sound by updating status first
            setCallStatus(CallStatuses.IN_PROGRESS);

            // Only connect if we have pending credentials
            if (pendingCallCredentials && activeCallData) {
                await connectToLiveKitRoom(pendingCallCredentials.url, pendingCallCredentials.token);

                // Remove duplicate setCallStatus - already set above
                const response = await updateCallStatus({
                    callId: activeCallData.callID,
                    partyId: activeCallData.partyID,
                    callStatus: CallType.HUMAN_ON_CALL,
                });
                console.log('Update call status response:', response);
                info(
                    'Updated call status successfully:',
                    { response, activeCallID: activeCallData.callID },
                    'LiveKitProvider',
                    'update_call_status_successfully'
                );

                // If we haven't gotten qualifications yet, try again
                const hasQualifications = aggregatedTagsUsed
                    ? aggregatedQualificationsData.length > 0
                    : qualificationsData.length > 0;

                if (!hasQualifications && activeCallData.callID) {
                    retryFetchQualifications(activeCallData.callID, aggregatedTagsUsed);
                }

                // Clear pending credentials
                setPendingCallCredentials(null);
            } else {
                console.error('No pending call credentials available');
            }
        } catch (error) {
            console.error('Error accepting call:', error);
            toast.error(`Error accepting call: ${error instanceof Error ? error.message : String(error)}`);
            logError(
                'Error accepting call:',
                error as Error,
                { activeCallData },
                'LiveKitProvider',
                'accept_call_error'
            );
            resetCallData();
        }
    }, [pendingCallCredentials, activeCallData, qualificationsData, aggregatedQualificationsData, aggregatedTagsUsed]);

    // Function to reject incoming call
    const rejectCall = useCallback(async () => {
        try {
            // Stop ringing sound and auto-reject timer
            resetCallTimeouts();

            // Update call status to ready
            setCallStatus(CallStatuses.READY);

            // Get call data from ref to ensure we have the latest data
            const currentCallData = activeCallDataRef.current;

            // Check if we have call data to work with
            if (!currentCallData || !currentCallData.callID) {
                warn(
                    'No valid call data available when rejecting call',
                    { currentCallData },
                    'LiveKitProvider',
                    'no_valid_call_data_available_when_rejecting_call'
                );
                return;
            }

            await updateCallStatus({
                callId: currentCallData.callID,
                partyId: currentCallData.partyID,
                callStatus: CallType.AGENT_REJECTED,
            });

            // Disconnect from room if connected
            if (room && room.state === ConnectionState.Connected) {
                room.disconnect();
                info(
                    'Disconnected from room in rejectCall',
                    { room },
                    'LiveKitProvider',
                    'disconnected_from_room_in_reject_call'
                );
            }

            // Reset states (status already set above)
            console.log('Resetting active call data 2');
            setActiveCallData(null);
            setPendingCallCredentials(null);

            // Reset call related data
            setCallTranscript([]);
            setCallDetails(null);
            setAvailableStatuses([]);
            setPartyInfo(null);
            setQualificationsData([]);
            setAggregatedTagsUsed(false);
            setAggregatedQualificationsData([]);
            setIsQualificationsLoading(false);

            info('Rejected incoming call', { currentCallData }, 'LiveKitProvider', 'reject_call');
        } catch (error) {
            console.error('Error rejecting call:', error);
            resetCallData();
        }
    }, [room]);

    // Function to mute audio
    const mute = async () => {
        console.log('Muting microphone');
        if (room?.localParticipant) {
            await room.localParticipant.setMicrophoneEnabled(false);
            info('Microphone muted', { room }, 'LiveKitProvider', 'microphone_muted');
        }
    };

    // Function to unmute audio
    const unmute = async () => {
        console.log('Unmuting microphone');
        if (room?.localParticipant) {
            await room.localParticipant.setMicrophoneEnabled(true);
            info('Microphone unmuted', { room }, 'LiveKitProvider', 'microphone_unmuted');
        }
    };

    // Function to hang up call
    const hangUp = useCallback(async () => {
        console.log('Hanging up call');

        if (room) {
            // Disable microphone
            if (room.localParticipant) {
                await room.localParticipant.setMicrophoneEnabled(false);
                console.log('Microphone disabled');
            }

            // Disconnect from room
            room.disconnect();
            info(
                'Hang up call: Disconnected from room',
                { room, activeCallID: activeCallData?.callID },
                'LiveKitProvider',
                'disconnected_from_room'
            );
        } else {
            resetCallData();
        }
    }, [room]);

    // Function to update agent availability
    const updateAgentAvailability = async (isAvailable: boolean, callStatus: CallStatusType = CallStatuses.READY) => {
        await updateAgentAvailabileService(isAvailable, {
            onSuccess: () => {
                // Update internal state to track agent availability
                setAgentAvailable(isAvailable);
                setCallStatus(callStatus);
            },
            onError: () => {
                toast.error('Error updating agent availability');
            },
        });
    };

    const makeCall = useCallback(
        async (
            callId: string,
            { onSuccess, onError }: { onSuccess?: () => void; onError?: (error: Error) => void } = {}
        ) => {
            info('Making call', { callId }, 'LiveKitProvider', 'make_call');
            // Update agent availability to true if it's not already
            if (!agentAvailable) {
                await updateAgentAvailability(true, CallStatuses.MANUAL_CALLBACK);
            } else {
                setCallStatus(CallStatuses.MANUAL_CALLBACK);
            }

            // Get calldetails
            getCallDetails(callId, {
                onSuccess: (data) => {
                    getCallTranscriptWithRetry(callId, {
                        onSuccess: (transcriptData) => {
                            setCallTranscript(formatTranscriptData(transcriptData));
                        },
                        onError: (error) => {
                            console.error('Error fetching call transcript:', error);
                            setCallTranscript([]);
                            info(
                                'Error fetching call transcript:',
                                { activeCallData: activeCallDataRef.current },
                                'LiveKitProvider',
                                'error_fetching_call_transcript'
                            );
                        },
                    });

                    // Store call details
                    setCallDetails(data.callDetails);

                    // Store available statuses
                    setAvailableStatuses(data.statuses || []);

                    // Store party info
                    setPartyInfo(data.partyInfo);
                    setAggregatedTagsUsed(data.aggregatedTagsUsed);

                    // Fetch qualifications with retry
                    retryFetchQualifications(callId, data.aggregatedTagsUsed);
                },
                onError: (error) => {
                    console.error('Error fetching call details:', error);
                },
            });

            const errorHandler = (error: Error) => {
                toast.error('Error initiating manual callback');
                setCallStatus(CallStatuses.READY);
                stopBeeping();
                onError && onError(error);
            };

            // Initiate manual callback
            await initiateManualCallback(callId, {
                onSuccess: async ({ livekitCreds }) => {
                    info('Manual callback initiated', { callId }, 'LiveKitProvider', 'manual_callback_initiated');

                    setActiveCallData({
                        callID: livekitCreds.CallID,
                        partyID: livekitCreds.PartyID,
                        roomName: livekitCreds.Room,
                        isManualCallback: true,
                    });

                    try {
                        // Connect to LiveKit room immediately for outbound calls
                        await connectToLiveKitRoom(livekitCreds.URL, livekitCreds.Token);
                        startBeeping();

                        // Update status to in progress once connected
                        setCallStatus(CallStatuses.IN_PROGRESS);
                        onSuccess && onSuccess();
                    } catch (error) {
                        logError(
                            'Error connecting to LiveKit room',
                            error as Error,
                            { callId: livekitCreds.CallID, livekitURL: livekitCreds.URL },
                            'LiveKitProvider',
                            'error_connecting_to_livekit_room'
                        );
                        errorHandler(error as Error);
                    }
                },
                onError: errorHandler,
            });
        },
        [initiateManualCallback, agentAvailable]
    );

    // Format client info from call details
    const getFormattedClientInfo = () => {
        if (!callDetails)
            return {
                name: 'Waiting for call...',
                phoneNumber: '',
                workflow: '',
                isParentCall: false,
            };

        const isParentCall = !!callDetails.parentCallDetails;

        return {
            name: `${callDetails.firstName || ''} ${callDetails.lastName || ''}`.trim() || 'Unknown',
            phoneNumber: callDetails.cellPhone || '',
            workflow: callDetails.workflowName || '',
            callDirection: callDetails.callDirection || '',
            ...(isParentCall && {
                isParentCall,
                parentCallDate: formatRelative(new Date(callDetails.parentCallDetails?.createdAt || ''), new Date()),
                parentWorkflowName: callDetails.parentCallDetails?.workflowName || '',
            }),
        };
    };

    // Monitor key state changes that could cause re-renders
    useEffect(() => {
        const connectionStatus = {
            [ReadyState.CONNECTING]: 'Connecting',
            [ReadyState.OPEN]: 'Open',
            [ReadyState.CLOSING]: 'Closing',
            [ReadyState.CLOSED]: 'Closed',
            [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
        }[readyState];

        info(
            'LiveKitProvider state change detected:',
            { callStatus, readyState, activeCallData, connectionStatus },
            'LiveKitProvider',
            'state_change'
        );
    }, [callStatus, readyState, activeCallData]);

    // Context value
    const contextValue = {
        room,
        callStatus,
        transferTarget,
        activeCallData,
        agentAvailable,
        callTranscript,
        callDetails,
        availableStatuses,
        partyInfo,
        qualifications: qualificationsData,
        aggregatedTagsUsed,
        aggregatedQualifications: aggregatedQualificationsData,
        isQualificationsLoading,
        clientInfo: getFormattedClientInfo(),
        mute,
        unmute,
        hangUp,
        acceptCall,
        rejectCall,
        makeCall,
        updateAgentAvailability,
        wsReadyState: readyState,
        shouldCloseCallConsole,
        setShouldCloseCallConsole,
        forceReconnect,
    };

    return <LiveKitContext.Provider value={contextValue}>{children}</LiveKitContext.Provider>;
};

// Custom hook to use LiveKit context
export const useLiveKit = () => {
    const context = useContext(LiveKitContext);

    if (!context) {
        throw new Error('useLiveKit must be used within a LiveKitProvider');
    }

    return context;
};

export default LiveKitContext;
